import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { IconEye, IconEyeOff, IconArrowRight, IconApps, IconFolder, IconUsers, IconChartBar, IconCheck } from '@tabler/icons-react';
import { apiService } from '../services/api';
import { LoginRequest } from '../types';
import './LoginForm.css';
import './LoginEntry.css';
import { storage } from '../utils/localStorage';

const GOOGLE_GSI_SRC = 'https://accounts.google.com/gsi/client';

/** Loads the Google Identity Services script once; later calls share the same promise. */
let gsiLoading: Promise<void> | null = null;
const loadGoogleIdentity = (): Promise<void> => {
  if (window.google?.accounts?.id) {
    return Promise.resolve();
  }
  if (!gsiLoading) {
    gsiLoading = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = GOOGLE_GSI_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => {
        gsiLoading = null;
        reject(new Error('Google Identity Services script failed to load'));
      };
      document.head.appendChild(script);
    });
  }
  return gsiLoading;
};

interface LoginFormProps {
  onLoginSuccess?: (token: string) => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onLoginSuccess }) => {
  const savedEmail = storage.getItem('savedEmail') || '';
  const savedPassword = storage.getItem('savedPassword') || '';
  const autoLogin = storage.getItem('autoLogin');

  const [credentials, setCredentials] = useState<LoginRequest>({
    email: savedEmail,
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rememberMe, setRememberMe] = useState(!!autoLogin);
  const [showPassword, setShowPassword] = useState(false);
  const formRef = React.useRef<HTMLFormElement>(null);
  const googleButtonRef = React.useRef<HTMLDivElement>(null);
  // Read per render rather than at module load so tests can flip the variable.
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

  useEffect(() => {
    const autoLogin = storage.getItem('autoLogin');

    if (autoLogin && savedEmail && savedPassword) {
      setCredentials({
        email: savedEmail,
        password: savedPassword,
      });

      const timer = setTimeout(() => {
        formRef.current?.requestSubmit();
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [savedEmail, savedPassword]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  /** Shared tail of every successful login: persist the session and hand the token up. */
  const finishLogin = (responseData: any, remember: { email: string; password: string } | null): boolean => {
    const token = responseData.data?.token || responseData.token;
    const user = responseData.data?.user || responseData.user;

    if (!token) {
      setError('Token not received from server');
      return false;
    }

    storage.setItem('token', token);
    if (user) {
      storage.setItem('user', JSON.stringify(user));
    }

    if (remember) {
      storage.setItem('savedEmail', remember.email);
      storage.setItem('savedPassword', remember.password);
      if (rememberMe) {
        storage.setItem('autoLogin', 'true');
      } else {
        storage.removeItem('autoLogin');
      }
    } else {
      // A Google session has no password to replay, so a stale auto-login must not kick in.
      storage.removeItem('savedPassword');
      storage.removeItem('autoLogin');
    }

    onLoginSuccess?.(token);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.login(credentials);

      if (response.success) {
        finishLogin(response.data as any, credentials);
      } else {
        setError(response.message || 'Prihlásenie zlyhalo');
      }
    } catch (err) {
      setError('Chyba pripojenia');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleCredential = useCallback(async (response: GoogleCredentialResponse) => {
    setLoading(true);
    setError(null);

    try {
      const result = await apiService.loginWithGoogle(response.credential);

      if (result.success) {
        finishLogin(result.data as any, null);
      } else {
        setError(result.message || 'Prihlásenie cez Google zlyhalo');
      }
    } catch (err) {
      setError('Chyba pripojenia');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onLoginSuccess, rememberMe]);

  // "Sign in with Google" button: only when a client id is configured for this build.
  useEffect(() => {
    if (!googleClientId || !googleButtonRef.current) {
      return;
    }
    let cancelled = false;
    let resizeObserver: ResizeObserver | undefined;
    const target = googleButtonRef.current;

    loadGoogleIdentity()
      .then(() => {
        const id = window.google?.accounts?.id;
        if (cancelled || !id) {
          return;
        }
        id.initialize({ client_id: googleClientId, callback: handleGoogleCredential });
        let renderedWidth = 0;
        const renderButton = () => {
          const width = target.clientWidth || 320;
          if (cancelled || width === renderedWidth) return;
          renderedWidth = width;
          target.replaceChildren();
          id.renderButton(target, {
            theme: 'outline',
            size: 'large',
            width,
            text: 'signin_with',
            locale: 'sk',
          });
        };
        renderButton();
        if (typeof ResizeObserver !== 'undefined') {
          resizeObserver = new ResizeObserver(renderButton);
          resizeObserver.observe(target);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('Prihlásenie cez Google sa nepodarilo načítať');
        }
      });

    return () => {
      cancelled = true;
      resizeObserver?.disconnect();
    };
  }, [googleClientId, handleGoogleCredential]);

  return (
    <div className="login-entry">
      <div className="login-entry-card">
        <aside className="login-entry-story">
          <div className="v5-hero-brand">
            flowy<span className="v5-brand-dot">.</span>
          </div>
          <div className="login-entry-intro">
            <span className="login-entry-eyebrow">VÁŠ PRACOVNÝ PRIESTOR</span>
            <h2>Menej klikania.<br/><span>Viac hotovej práce.</span></h2>
            <p>Aplikácie, projekty a prehľady. Všetko máte poruke.</p>
          </div>

          <div className="login-entry-visual" aria-hidden="true">
            <svg className="login-visual-lines" viewBox="0 0 400 220" fill="none" preserveAspectRatio="none">
              <circle cx="200" cy="110" r="86"/>
              <circle cx="200" cy="110" r="55"/>
              <path d="M66 62H117Q130 62 130 75V97Q130 110 143 110H200"/>
              <path d="M320 52H275Q262 52 262 65V97Q262 110 249 110H200"/>
              <path d="M320 168H275Q262 168 262 155V123Q262 110 249 110H200"/>
              <path d="M80 175H117Q130 175 130 162V123Q130 110 143 110H200"/>
            </svg>
            <div className="login-visual-core"><IconApps size={28}/></div>
            <div className="login-visual-node is-projects"><IconFolder size={23}/></div>
            <div className="login-visual-node is-people"><IconUsers size={23}/></div>
            <div className="login-visual-node is-insights"><IconChartBar size={23}/></div>
            <div className="login-visual-check"><IconCheck size={16}/></div>
          </div>

          <div className="login-entry-story-note"><span>Projekty</span><span>Aplikácie</span><span>Prehľady</span></div>
        </aside>

        <main className="v5-login-panel">
          <div className="v5-login-form-wrap">
            <div className="login-entry-mobile-brand">flowy<span className="v5-brand-dot">.</span></div>
            <div className="v5-login-form-head">
              <span className="login-entry-eyebrow">PRIHLÁSENIE</span>
              <h1 className="v5-login-title">Vitajte späť</h1>
              <p className="v5-login-subtitle">Prihláste sa a pokračujte tam, kde ste skončili.</p>
            </div>

            {error && <div className="v5-login-error" role="alert">{error}</div>}

            <form onSubmit={handleSubmit} ref={formRef} aria-busy={loading}>
              <div className="v5-login-field">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  className="form-control"
                  placeholder="vas@email.sk"
                  value={credentials.email}
                  onChange={handleInputChange}
                  required
                  autoComplete="username"
                />
              </div>

              <div className="v5-login-field">
                <div className="login-entry-password-label">
                  <label htmlFor="password">Heslo</label>
                  <Link to="/forgot-password">Zabudnuté heslo?</Link>
                </div>
                <div className="v5-login-pass">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    className="form-control"
                    placeholder="Vaše heslo"
                    value={credentials.password}
                    onChange={handleInputChange}
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="v5-login-eye"
                    onClick={() => setShowPassword(prev => !prev)}
                    aria-label={showPassword ? 'Skryť heslo' : 'Zobraziť heslo'}
                    aria-pressed={showPassword}
                    aria-controls="password"
                  >
                    {showPassword ? <IconEyeOff size={18}/> : <IconEye size={18}/>}
                  </button>
                </div>
              </div>

              <label className="v5-login-remember">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span>Zapamätať si ma</span>
              </label>

              <button type="submit" className="v5-login-submit" disabled={loading}>
                {loading ? 'Prihlasovanie…' : (
                  <>
                    Prihlásiť sa
                    <IconArrowRight size={18} className="v5-login-submit-arrow"/>
                  </>
                )}
              </button>

            </form>

            {googleClientId && (
              <div className="v5-login-google" data-testid="google-login">
                <div className="v5-login-divider"><span>alebo</span></div>
                <div className="v5-login-google-button" ref={googleButtonRef} aria-label="Prihlásiť sa cez Google"/>
              </div>
            )}
          </div>
        </main>
      </div>
      <footer className="login-entry-footer">© Flowy, a.s.</footer>
    </div>
  );
};
